import React, { useState } from 'react';

const STEP_TYPE_BADGE = {
  data_retrieval: { label: 'Data Retrieval', cls: 'ev-message-card2' },
  analysis:       { label: 'Analysis',       cls: 'ev-message-card2' },
  final_response: { label: 'Final Response', cls: 'ev-message-card3-green' },
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <button
      type="button"
      className="ev-btn-light-fill ev-btn-xsmall ev-radius-xsmall"
      onClick={handleCopy}
    >
      <i className={`bi ${copied ? 'bi-check2' : 'bi-copy'} me-1`}></i>
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
};

/* ── Retrieved Chunks (GenBI-identical) ─────────────────────────────────── */

const ChunkContent = ({ chunk, index }) => {
  const [expanded, setExpanded] = useState(false);
  const sim = chunk.cosine_similarity || 0;
  const cc = chunk.current_chunk || {};
  const content = cc.chunk_content || '';
  const matchPct = sim > 0 ? `${(sim * 100).toFixed(1)}% match` : '';
  const isLong = content.length > 250;

  return (
    <div className="ev-card-2 p-2 ev-radius-xsmall mb-2">
      <div className="d-flex justify-content-between align-items-center mb-1">
        <span className="ev-xsmall-p-dark opacity-75">Chunk {index + 1}</span>
        {matchPct && (
          <span className="ev-message-card1 ev-btn-xsmall ev-radius-xsmall">{matchPct}</span>
        )}
      </div>
      <p className="ev-xsmall-p-dark mb-0" style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
        {expanded || !isLong ? content : content.slice(0, 250) + '...'}
      </p>
      {isLong && (
        <button
          type="button"
          className="ev-small-p-blue border-0 bg-transparent p-0 mt-1"
          style={{ cursor: 'pointer', fontSize: 'var(--px-12)' }}
          onClick={() => setExpanded(p => !p)}
        >
          {expanded ? 'Show less' : 'Show more'}
        </button>
      )}
    </div>
  );
};

const DocCard = ({ doc }) => {
  const chunks = doc.chunks || [];
  const docTitle = doc['document title'] || doc.document_title || `Document ${doc.doc_id}`;

  return (
    <div className="ev-card-8 p-2 ev-radius-small mb-2">
      <div className="d-flex align-items-start gap-2 mb-2">
        <span className="ev-message-card1 ev-btn-xsmall ev-radius-xsmall text-nowrap flex-shrink-0">
          doc_{doc.doc_id}
        </span>
        <span className="ev-bold-med ev-xsmall-p-dark">{docTitle}</span>
      </div>
      {chunks.map((chunk, i) => (
        <ChunkContent key={i} chunk={chunk} index={i} />
      ))}
    </div>
  );
};

const RetrievedChunksSection = ({ retrievedChunks }) => {
  const [open, setOpen] = useState(false);
  if (!retrievedChunks || retrievedChunks.length === 0) return null;

  const totalChunks = retrievedChunks.reduce((sum, doc) => sum + (doc.chunks?.length || 0), 0);

  return (
    <div className="mt-2">
      <div
        className="d-flex align-items-center gap-2"
        style={{ cursor: 'pointer' }}
        onClick={() => setOpen(p => !p)}
      >
        <i className={`bi bi-chevron-${open ? 'up' : 'down'} ev-small-p-blue`}></i>
        <i className="bi bi-file-earmark-text ev-small-p-blue"></i>
        <span className="ev-bold-med ev-xsmall-p-dark">
          Retrieved Chunks ({totalChunks} chunk{totalChunks !== 1 ? 's' : ''} from {retrievedChunks.length} doc{retrievedChunks.length !== 1 ? 's' : ''})
        </span>
      </div>
      {open && (
        <div className="mt-2">
          {retrievedChunks.map((doc, i) => (
            <DocCard key={i} doc={doc} />
          ))}
        </div>
      )}
    </div>
  );
};

/* ── Tool Card (identical to AgentLogModal.ToolCard) ────────────────────── */

const ToolCard = ({ tool }) => {
  const [open, setOpen] = useState(false);
  const hasContent = tool.generated_code || tool.result_summary || tool.error
    || tool.inputs || tool.retrieved_chunks || (tool.log_timeline && tool.log_timeline.length > 0);

  return (
    <div className="ev-card-8 mb-2 p-2 ev-radius-small">
      <div
        className="d-flex align-items-center justify-content-between"
        style={{ cursor: hasContent ? 'pointer' : 'default' }}
        onClick={() => hasContent && setOpen(p => !p)}
      >
        <div className="d-flex align-items-center gap-2">
          <span className="ev-bold-med ev-small-p-dark">{tool.tool}</span>
        </div>
        <div className="d-flex align-items-center gap-2">
          {hasContent && <i className={`bi bi-chevron-${open ? 'up' : 'down'} ev-small-p-blue`}></i>}
        </div>
      </div>

      {open && (
        <div className="mt-2 pt-2 border-top">
          {tool.inputs && (
            <div className="mb-2">
              {tool.inputs.query && (
                <div className="ev-message-card1 ev-btn-xsmall d-inline-block mb-1 ev-radius-xsmall">
                  Query: {tool.inputs.query}
                </div>
              )}
              {tool.inputs.doc_ids?.length > 0 && (
                <div className="ev-small-p-dark mb-1">
                  Docs: {(() => {
                    const rc = tool.retrieved_chunks || [];
                    const nameMap = {};
                    rc.forEach(d => { if (d.doc_id) nameMap[d.doc_id] = d['document title'] || d.document_title; });
                    return tool.inputs.doc_ids.map(id => nameMap[id] || `doc_${id}`).join(', ');
                  })()}
                </div>
              )}
              {tool.inputs.doc_sources?.length > 0 && (
                <div className="ev-small-p-dark mb-1">
                  Sources: {tool.inputs.doc_sources.map(d => `doc_${d.doc_id}`).join(', ')}
                </div>
              )}
              {tool.inputs.image_count > 0 && (
                <div className="ev-small-p-dark mb-1">
                  <i className="bi bi-images me-1"></i>{tool.inputs.image_count} image(s) analysed
                </div>
              )}
            </div>
          )}

          {tool.error && (
            <div className="ev-alert-card-warning mb-2 ev-radius-xsmall">
              <b className="bi bi-exclamation-triangle me-1"> Error:</b>{tool.error}
            </div>
          )}

          {/* Retrieved Chunks — GenBI identical rendering */}
          {tool.retrieved_chunks && tool.retrieved_chunks.length > 0 && (
            <RetrievedChunksSection retrievedChunks={tool.retrieved_chunks} />
          )}

          {tool.result_summary && !tool.generated_code && (
            <div className="mb-2">
              <div className="ev-bold-med ev-xsmall-p-dark mb-1">
                <i className="bi bi-clipboard-data me-1"></i>Result
              </div>
              <pre className="ev-card-2 ev-radius-xsmall ev-small-p-dark p-2" style={{ fontSize: 'var(--px-11)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace', maxHeight: '300px', overflowY: 'auto' }}>
                {tool.result_summary}
              </pre>
            </div>
          )}

          {!tool.generated_code && tool.log_timeline && tool.log_timeline.length > 0 && (
            <div className="mb-2">
              <div className="ev-bold-med ev-xsmall-p-dark mb-1">
                <i className="bi bi-journal-text me-1"></i>Processing Log
              </div>
              <div className="ev-card-2 p-2 ev-radius-xsmall" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                {tool.log_timeline.map((entry, i) => (
                  <div key={i} className="d-flex align-items-start gap-2 mb-1">
                    <i className={`bi ${
                      entry.log_type === 'success' ? 'bi-check-circle-fill text-success' :
                      entry.log_type === 'error' ? 'bi-x-circle-fill text-danger' :
                      entry.log_level === 'phase' ? 'bi-arrow-right-circle-fill ev-small-p-blue' :
                      'bi-dot opacity-50'
                    }`} style={{ fontSize: 'var(--px-10)', marginTop: '3px', flexShrink: 0 }}></i>
                    <span className={`ev-xsmall-p-dark ${entry.log_level === 'phase' ? 'ev-bold-med' : 'opacity-75'}`}>
                      {entry.message}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tool.generated_code && tool.log_timeline && tool.log_timeline.length > 0 && (
            <div className="mb-2">
              <div className="ev-bold-med ev-xsmall-p-dark mb-1">
                <i className="bi bi-journal-text me-1"></i>Processing Log
              </div>
              <div className="ev-card-2 p-2 ev-radius-xsmall" style={{ maxHeight: '250px', overflowY: 'auto' }}>
                {tool.log_timeline.map((entry, i) => (
                  <div key={i} className="d-flex align-items-start gap-2 mb-1">
                    <i className={`bi ${
                      entry.log_type === 'success' ? 'bi-check-circle-fill text-success' :
                      entry.log_type === 'error' ? 'bi-x-circle-fill text-danger' :
                      entry.log_level === 'phase' ? 'bi-arrow-right-circle-fill ev-small-p-blue' :
                      'bi-dot opacity-50'
                    }`} style={{ fontSize: 'var(--px-10)', marginTop: '3px', flexShrink: 0 }}></i>
                    <span className={`ev-xsmall-p-dark ${entry.log_level === 'phase' ? 'ev-bold-med' : 'opacity-75'}`}>
                      {entry.message}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}


          {tool.generated_code && (
            <div className="mb-2">
              <div className="d-flex align-items-center justify-content-between mb-1">
                <div className="ev-bold-med ev-xsmall-p-dark">
                  <i className="bi bi-code-slash me-1"></i>workbook_builder.py
                </div>
                <CopyButton text={tool.generated_code} />
              </div>
              <pre className="ev-card-2 ev-radius-xsmall ev-small-p-dark scrollable-element vh-30 p-2" style={{ fontSize: 'var(--px-11)', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: 'monospace' }}>
                {tool.generated_code}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Chain Accordion (identical to AgentLogModal.ChainAccordion) ────────── */

const ChainAccordion = ({ step, index }) => {
  const [open, setOpen] = useState(false);
  const badge = STEP_TYPE_BADGE[step.type] || STEP_TYPE_BADGE.analysis;
  const toolCount = step.tool_executions?.length || 0;

  return (
    <div className="mb-2">
      <button
        type="button"
        className="w-100 d-flex align-items-center justify-content-between ev-message-card2 border-0 text-start"
        style={{ borderRadius: open ? 'var(--px-10) var(--px-10) 0 0' : 'var(--px-10)', padding: 'var(--px-10) var(--px-15)' }}
        onClick={() => setOpen(p => !p)}
      >
        <div className="d-flex align-items-center gap-2 mw-0">
          <span className="ev-bold-med ev-xsmall-p-dark text-nowrap">Chain {index + 1}</span>
          <span className={`${badge.cls} ev-btn-xsmall ev-radius-xlarge text-nowrap`}>{badge.label}</span>
          {toolCount > 0 && (
            <span className="ev-xsmall-p-dark text-nowrap">
              {toolCount} tool{toolCount > 1 ? 's' : ''}
            </span>
          )}
        </div>
        <i className={`bi bi-chevron-${open ? 'up' : 'down'} ev-small-p-blue ms-2`}></i>
      </button>

      {open && (
        <div className="ev-card-2 ev-padding-small ev-radius-xsmall" style={{borderTopLeftRadius: 0, borderTopRightRadius: 0}}>
          {step.description && (
            <div className="mb-3">
              <div className="ev-bold-med ev-small-p-blue mb-1">
                <i className="bi bi-card-text me-1"></i>Description
              </div>
              <p className="ev-small-p-dark mb-0 lh-lg">{step.description}</p>
            </div>
          )}

          {step.thinking && step.type !== 'final_response' && (
            <div className="mb-3">
              <div className="ev-bold-med ev-small-p-blue mb-1">
                <i className="bi bi-lightbulb me-1"></i>Reasoning
              </div>
              <p className="ev-small-p-dark mb-0" style={{whiteSpace: 'pre-wrap'}}>{step.thinking}</p>
            </div>
          )}

          {toolCount > 0 && (
            <div>
              <div className="ev-bold-med ev-small-p-blue mb-2">
                Tool Executions
              </div>
              {step.tool_executions.map((t, i) => <ToolCard key={i} tool={t} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

/* ── Main Component ─────────────────────────────────────────────────────── */

const CopilotThinkingLogs = ({ thinkingLogs }) => {
  if (!thinkingLogs || !thinkingLogs.steps || thinkingLogs.steps.length === 0) {
    return null;
  }

  const nonFinalSteps = thinkingLogs.steps.filter(s => s.type !== 'final_response');

  return (
    <div className="copilot-thinking-logs-container">
      <div className="d-flex align-items-center gap-2 mb-3">
        <i className="bi bi-journal-text ev-small-p-blue"></i>
        <span className="ev-bold-med ev-reg-p-dark">Execution Logs</span>
      </div>

      <div className="d-flex align-items-center gap-2 mb-2">
        <span className="ev-message-card3 ev-btn-xsmall">
          <i className="bi bi-link-45deg me-1"></i>
          {nonFinalSteps.length} chain{nonFinalSteps.length !== 1 ? 's' : ''}
        </span>
      </div>

      {nonFinalSteps.map((step, i) => (
        <ChainAccordion key={step.step || i} step={step} index={i} />
      ))}
    </div>
  );
};

export default CopilotThinkingLogs;
